type AuthKey =
  | 'accessToken'
  | 'refreshToken'
  | 'userId'
  | 'userEmail'
  | 'userName'
  | 'userAvatar'
  | 'userBalance';

const AUTH_KEYS: AuthKey[] = [
  'accessToken',
  'refreshToken',
  'userId',
  'userEmail',
  'userName',
  'userAvatar',
  'userBalance',
];

function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage write failures.
  }
}

function safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage removal failures.
  }
}

export function getAuthItem(key: AuthKey): string {
  const sessionValue = safeGet(sessionStorage, key);
  if (sessionValue !== null) {
    return sessionValue;
  }

  const legacyValue = safeGet(localStorage, key);
  if (legacyValue !== null) {
    safeSet(sessionStorage, key, legacyValue);
    safeRemove(localStorage, key);
    return legacyValue;
  }

  return '';
}

export function getAuthNumber(key: Extract<AuthKey, 'userBalance'>): number {
  const value = Number(getAuthItem(key));
  return Number.isFinite(value) ? value : 0;
}

export function setAuthItem(key: AuthKey, value: string | number): void {
  const serialized = String(value);
  safeSet(sessionStorage, key, serialized);
  safeRemove(localStorage, key);
}

export function setAuthSession(session: Partial<Record<AuthKey, string | number>>): void {
  AUTH_KEYS.forEach((key) => {
    const value = session[key];
    if (value !== undefined) {
      setAuthItem(key, value);
    }
  });
}

export function clearAuthSession(): void {
  AUTH_KEYS.forEach((key) => {
    safeRemove(sessionStorage, key);
    safeRemove(localStorage, key);
  });
  safeRemove(localStorage, 'rememberCpf');
}

export function getAuthToken(): string {
  return getAuthItem('accessToken');
}

export function isAuthenticated(): boolean {
  return getAuthToken().trim().length > 0;
}
